import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Prompt } from 'react-router-dom';

import FetchingInfoScreen from './FetchingInfoScreen';
import ImgViewer from './ImgViewer';
import MalwareDetectedScreen from './MalwareDetectedScreen';
import { NoFileToDisplayScreen } from './NoFileToDisplayScreen';
import { ProcessingFailedScreen } from './ProcessingFailedScreen';
import UnsupportedFileScreen from './UnsupportedFileScreen';
import VideoViewer from './VideoViewer';
import CADViewer from './cadViewer/CADViewer';
import {
  getAreCaliperToolSelectionsPresent,
  getAreSelectorToolSelectionsPresent,
} from './cadViewer/data/reducer';
import {
  getActiveFile,
  getActivePartPmiUrl,
  getIsFetchingViewerModel,
} from './data/reducer';
import { PDFViewer } from './pdfViewer/PDFViewer';
import { getAreUnsavedPdfAnnotationsPresent } from './pdfViewer/data/reducer';
import type { ViewerConfig } from './pdfViewer/viewerConfig';
import { getViewerDisplayParameters } from './utils';

type ViewerProps = {
  config: ViewerConfig;
  leftOffset?: string;
};

const Viewer = ({ leftOffset, config }: ViewerProps) => {
  const activeFile = useSelector(getActiveFile);
  const activePartPmiUrl = useSelector(getActivePartPmiUrl);
  const areCaliperToolSelectionsPresent = useSelector(
    getAreCaliperToolSelectionsPresent,
  );
  const areSelectorToolSelectionsPresent = useSelector(
    getAreSelectorToolSelectionsPresent,
  );
  const areUnsavedPdfAnnotationsPresent = useSelector(
    getAreUnsavedPdfAnnotationsPresent,
  );
  const isFetchingViewerModel = useSelector(getIsFetchingViewerModel);

  const {
    containsVirus,
    filename,
    processingFailed,
    standardizedUrl,
    url,
    uuid,
  } = activeFile ?? {};
  const {
    shouldShowCAD,
    shouldShowPdf,
    shouldShowNoFileToDisplayScreen,
    isImg,
    isVideo,
  } = useMemo(() => getViewerDisplayParameters(activeFile ?? {}), [activeFile]);

  const [shouldLoadCAD, setShouldLoadCAD] = useState(shouldShowCAD);
  const [hasShownFetching, setHasShownFetching] = useState(false);
  const [pmiUrlByPartFileUuid, setPmiUrlByPartFileUuid] = useState({});

  // This mounts the CAD Viewer "at most once" when any PartFile with geometry is viewed
  // The CAD Viewer is hidden, rather than unmounting, when other viewers are used (when new PartFile does not have geometry, i.e. CAD -> PDF)
  useEffect(() => {
    if (!shouldLoadCAD) {
      setShouldLoadCAD(shouldShowCAD);
    }
  }, [shouldLoadCAD, shouldShowCAD]);

  useEffect(() => {
    if (!uuid) {
      return;
    }
    const activePartFileUuid = uuid;
    if (!(activePartFileUuid in pmiUrlByPartFileUuid)) {
      setPmiUrlByPartFileUuid({
        ...pmiUrlByPartFileUuid,
        [activePartFileUuid]: activePartPmiUrl,
      });
    }
  }, [pmiUrlByPartFileUuid, activePartPmiUrl, uuid]);

  const [shouldRenderPDFViewer, setShouldRenderPDFViewer] =
    useState(shouldShowPdf);

  // This mounts the PDF Viewer "at most once" when any PartFile in the viewer is a PDF
  // The PDF Viewer is hidden, rather than unmounting, when other viewers are used
  useEffect(() => {
    if (!shouldRenderPDFViewer) {
      setShouldRenderPDFViewer(shouldShowPdf);
    }
  }, [shouldRenderPDFViewer, shouldShowPdf]);

  useEffect(() => {
    setHasShownFetching(
      (_hasShownFetching) => !isFetchingViewerModel && !_hasShownFetching,
    );
  }, [isFetchingViewerModel, setHasShownFetching]);

  let nonCadViewer = <></>;
  if (isImg) {
    nonCadViewer = <ImgViewer />;
  } else if (isVideo) {
    nonCadViewer = <VideoViewer />;
  } else if (shouldShowNoFileToDisplayScreen) {
    nonCadViewer = <NoFileToDisplayScreen key="noFileToDisplay" />;
  } else {
    nonCadViewer = <UnsupportedFileScreen key="UnsupportedFile" />;
  }

  return (
    <>
      <Prompt
        when={
          !!(
            areUnsavedPdfAnnotationsPresent ||
            areCaliperToolSelectionsPresent ||
            areSelectorToolSelectionsPresent
          )
        }
        message={
          'You have unsaved annotations. Are you sure you want to continue?'
        }
      />
      {containsVirus ? (
        <MalwareDetectedScreen />
      ) : (
        <>
          {isFetchingViewerModel && !hasShownFetching && <FetchingInfoScreen />}
          {shouldLoadCAD && (
            <CADViewer
              leftOffset={leftOffset}
              isPaused={!shouldShowCAD}
              pmiUrlByPartFileUuid={pmiUrlByPartFileUuid}
            />
          )}
          {shouldRenderPDFViewer && (
            <PDFViewer
              activeFileUuid={uuid ?? null}
              filename={filename ?? null}
              url={(standardizedUrl || url) ?? null}
              isPaused={!shouldShowPdf}
              config={config}
            />
          )}
          {processingFailed ? (
            <ProcessingFailedScreen key="ProcessingFailed" />
          ) : (
            !shouldShowCAD && nonCadViewer
          )}
        </>
      )}
    </>
  );
};

export default Viewer;
